import { toJS } from 'mobx';

/**
 * Storage class with namespace support to prevent localStorage collisions
 * between different apps (qs2, qs-status-frontend) running on the same domain.
 *
 * CRITICAL: Each app MUST use a unique namespace to prevent preferences from
 * overwriting each other during local development.
 */
class Store {
  _state: Record<string, any>;
  _namespace: string;

  constructor(namespace: string = 'default') {
    this._namespace = namespace;
    this._state = this.load();
  }

  // Get the namespaced localStorage key
  private getStorageKey(): string {
    return `store:${this._namespace}`;
  }

  // Load data from localStorage
  load() {
    return JSON.parse(localStorage.getItem(this.getStorageKey()) || '{}');
  }

  // Save data to localStorage
  save(data: any) {
    localStorage.setItem(this.getStorageKey(), JSON.stringify(toJS(data)));
  }

  // Clear localStorage for this namespace
  clear() {
    localStorage.removeItem(this.getStorageKey());
    this._state = {};
  }

  // Set data to localStorage
  set(key: string, value: any) {
    this._state[key] = value;
    this.save(this._state);
  }

  // Get data from localStorage
  get(key: string) {
    return this._state[key];
  }

  // Remove data from localStorage
  remove(key: string) {
    delete this._state[key];
    this.save(this._state);
  }
}

/**
 * Create a namespaced storage instance.
 * Each app should use its own namespace to prevent localStorage collisions.
 *
 * @example
 * // In apps/qs/src/storage.ts
 * export const Storage = createStorage('qs');
 *
 * // In apps/care/src/storage.ts
 * export const Storage = createStorage('care');
 */
function createStorage(namespace: string): Store {
  return new Store(namespace);
}

// Default instance (backward compatibility - but apps should create their own namespaced instances)
const Storage = new Store('default');
export default Storage;
export { Store, createStorage };
